import React, { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import moment from 'moment';
import { yupResolver } from '@hookform/resolvers/yup';
import Text from '../../../../../Components/Base/Text/Text';
import { joinClasses } from '../../../../../Utils/className.util';
import DateField from '../../../../../Components/Form/components/DateField';
import TextField from '../../../../../Components/Base/TextField/TextField';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import RadioListField from '../../../../../Components/Form/components/RadioListField';
import {
	HealthPlanContext,
	FieldNames,
} from '../../../../../Services/HealthBabyPlanDataContext';
import useTranslation from '../../../../../Hooks/useTranslation';
import NumberField from '../../../../../Components/Form/components/NumberField';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import FormContainer from '../../../../../Components/Form/FormContainer';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import { getDateDifferenceMoment } from '../utils/date';
//@ts-ignore
import styles from './CurrentPregnancyForm.module.scss';
import { getMomentDate } from '../../../../../Utils/dates.util';

const VALIDATION_TRANSLATION_PREFIX = 'validation.currentPregnancyForm';

type ITranslations = (key: string) => string | undefined;

const getValidationSchema = (t: ITranslations) =>
	yup.object({
		[FieldNames.CURRENT_PREGNANCY]: yup.object({
			currentPregnancyMethod: yup
				.mixed()
				.nullable()
				.required(t(`${VALIDATION_TRANSLATION_PREFIX}.currentPregnancyMethod`)),
			plannedDateOfBirth: yup
				.date()
				.transform((curr, orig) => (orig === '' ? null : curr))
				.nullable()
				.required(t(`${VALIDATION_TRANSLATION_PREFIX}.date`)),
			totalNumberOfPregnanciesIncludingCurrent: yup
				.string()
				.nullable()
				.required(
					t(
						`${VALIDATION_TRANSLATION_PREFIX}.totalNumberOfPregnanciesIncludingCurrent`,
					),
				),
		}),
	});

interface Props {
	onSuccess: () => void;
	patientId: string;
}

const CurrentPregnancyForm: React.FC<Props> = ({ onSuccess, patientId }) => {
	const { t } = useTranslation();
	const { data, setData } = useContext(HealthPlanContext);

	const defaultValues = {
		[FieldNames.CURRENT_PREGNANCY]: data[FieldNames.CURRENT_PREGNANCY] ?? {},
	};

	const formMethods = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(getValidationSchema(t)),
	});

	const fieldValues = formMethods.watch(FieldNames.CURRENT_PREGNANCY);

	const { weeks, days } = getDateDifferenceMoment({
		//@ts-ignore
		estimatedDateOfBirth: getMomentDate(fieldValues?.plannedDateOfBirth),
	});

	useEffect(() => {
		formMethods.setValue(
			`${FieldNames.CURRENT_PREGNANCY}.weekOfPregnancy`,
			weeks && days ? `${weeks} + ${days}` : '',
		);
	}, [weeks, days]);

	return (
		<ScrollContainer>
			<div className='col-12 col-lg-8'>
				<div className='page-panel mt-1'>
					<FormWrapper formMethods={formMethods}>
						<FormContainer name={FieldNames.CURRENT_PREGNANCY}>
							<Text
								tx='base.currentPregnancy.title'
								className={joinClasses([styles.title, 'my-3'])}
								firstUpper
							/>
							<RadioListField
								isRequired
								label='base.currentPregnancy.method'
								name='currentPregnancyMethod'
								data={[
									{
										title: 'base.spontaneousConception',
										value: 'spontaneousConception',
									},
									{ title: 'base.ivf', value: 'ivf' },
								]}
								inLine
							/>
							<DateField
								isRequired
								label='base.plannedDateOfBirth'
								name='plannedDateOfBirth'
								minDate={moment()}
								className='mb-3'
							/>
							<TextField
								//@ts-ignore
								disabled
								label='base.actualGestationalWeek'
								name='weekOfPregnancy'
								value={weeks && days ? `${weeks} + ${days}` : ''}
							/>
							<NumberField
								isRequired
								label='base.totalNumberOfPregnanciesIncludingCurrent'
								name='totalNumberOfPregnanciesIncludingCurrent'
								className='mb-3'
							/>
							<div className='d-flex mt-4 justify-content-between'>
								<SubmitButton
									title={'base.shared.continue'}
									onSubmit={fields => {
										setData(fields);
										onSuccess();
									}}
								/>
							</div>
						</FormContainer>
					</FormWrapper>
				</div>
			</div>
		</ScrollContainer>
	);
};

export default CurrentPregnancyForm;
